// --- React
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
// --- Actions & Hooks
import { usePermissions } from "../../utils/permissions";
// --- External UI Libraries
import { ExpandableMenu } from "@dtci/spellbook/ExpandableMenu";
import Icon from "@dtci/spellbook/Icon";
import {
  DualPanelLayout,
  PrimaryPanel,
  SecondaryPanel,
} from "@dtci/spellbook/DualPanelLayout";
// --- Components
import { AppFooter } from "tap/components/AppFooter";
import { TapNavMenuSection } from "./TapNavMenuSection";
// --- Utils
import { buildNavTo } from "./navStorage.js";
// --- Styles
import "./PageNavigation.scss";

/**
 * PageNavigation component responsible for rendering the layout and navigation menu.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - The content displayed in the primary panel.
 * @returns {JSX.Element} The rendered PageNavigation component.
 */
export const PageNavigation = ({ children }) => {
  const { isAdmin, isTapAdmin, isDigitalStoresUser, isJobTracker } =
    usePermissions();
  const location = useLocation();
  const [activePanel, setActivePanel] = useState(location.pathname);

  useEffect(() => {
    if (localStorage.getItem("activePanel") !== location.pathname) {
      localStorage.setItem("activePanel", location.pathname);
    }
    if (localStorage.getItem("activePanelSearch") !== location.search) {
      localStorage.setItem("activePanelSearch", location.search);
    }
    setActivePanel(location.pathname);
  }, [location.pathname, location.search]);

  return (
    <>
      <div>
        <DualPanelLayout>
          <PrimaryPanel>{children}</PrimaryPanel>
          <SecondaryPanel fill="theme" strokeWidth="thin">
            <br />
            <TapNavMenuSection
              path="/home"
              to={buildNavTo("/home")}
              isActive={activePanel === "/home"}
              mainSection={
                <Icon
                  altText="home"
                  faPackage="fas"
                  iconName="fa-home"
                  iconType="fa"
                  title="home"
                />
              }
            >
              Home
            </TapNavMenuSection>
            <br />
            {!isDigitalStoresUser && (
              <>
                <TapNavMenuSection
                  path="/windowSequence"
                  to={buildNavTo("/windowSequence")}
                  isActive={activePanel === "/windowSequence"}
                  mainSection={
                    <Icon
                      altText="windowSequence"
                      faPackage="fas"
                      iconName="fa-border-all"
                      iconType="fa"
                      title="windowSequence"
                    />
                  }
                >
                  Windows
                </TapNavMenuSection>
                <br />
              </>
            )}
            {!isDigitalStoresUser && (
              <>
                <TapNavMenuSection
                  path="/rateCards"
                  to={buildNavTo("/rateCards")}
                  isActive={activePanel === "/rateCards"}
                  mainSection={
                    <Icon
                      altText="rateCards"
                      faPackage="fas"
                      iconName="fa-sack-dollar"
                      iconType="fa"
                      title="rateCards"
                    />
                  }
                >
                  Rate Cards
                </TapNavMenuSection>
                <br />
              </>
            )}
            {!isDigitalStoresUser && (
              <>
                <TapNavMenuSection
                  path="/licensingJobs"
                  to={buildNavTo("/licensingJobs")}
                  isActive={activePanel === "/licensingJobs"}
                  mainSection={
                    <Icon
                      altText="licensingJobs"
                      faPackage="fas"
                      iconName="fa-money-bill-wave"
                      iconType="fa"
                      title="licensingJobs"
                    />
                  }
                >
                  Licensing Jobs
                </TapNavMenuSection>
                <br />
              </>
            )}
            {!isDigitalStoresUser && (
              <>
                <TapNavMenuSection
                  path="/pmxCuratedDatesAndPrices"
                  to={buildNavTo("/pmxCuratedDatesAndPrices")}
                  isActive={activePanel === "/pmxCuratedDatesAndPrices"}
                  mainSection={
                    <Icon
                      altText="pmxCuratedDatesAndPrices"
                      faPackage="fas"
                      iconName="fa-file-invoice-dollar"
                      iconType="fa"
                      title="pmxCuratedDatesAndPrices"
                    />
                  }
                >
                  PMX Prices and Dates
                </TapNavMenuSection>
                <br />
              </>
            )}
            <ExpandableMenu isExpanded>
              {!isDigitalStoresUser && (
                <>
                  <TapNavMenuSection
                    path="/updateEngine"
                    to={buildNavTo("/updateEngine")}
                    isActive={activePanel === "/updateEngine"}
                    mainSection={
                      <Icon
                        altText="updateEngine"
                        faPackage="fas"
                        iconName="fa-gears"
                        iconType="fa"
                        title="updateEngine"
                      />
                    }
                  >
                    Update Engine
                  </TapNavMenuSection>
                  <br />
                </>
              )}
              {isJobTracker && !isDigitalStoresUser && (
                <>
                  <TapNavMenuSection
                    path="/tapJobs"
                    to={buildNavTo("/tapJobs")}
                    isActive={activePanel === "/tapJobs"}
                    mainSection={
                      <Icon
                        altText="tapJobs"
                        faPackage="fas"
                        iconName="fa-briefcase"
                        iconType="fa"
                        title="tapJobs"
                      />
                    }
                  >
                    TAP Jobs
                  </TapNavMenuSection>
                  <br />
                </>
              )}
              {isJobTracker && !isDigitalStoresUser && (
                <>
                  <TapNavMenuSection
                    path="/pmxDeliveries"
                    to={buildNavTo("/pmxDeliveries")}
                    isActive={activePanel === "/pmxDeliveries"}
                    mainSection={
                      <Icon
                        altText="pmxDeliveries"
                        faPackage="fas"
                        iconName="fa-dollar-sign"
                        iconType="fa"
                        title="pmxDeliveries"
                      />
                    }
                  >
                    PMX Deliveries
                  </TapNavMenuSection>
                  <br />
                </>
              )}
              {(isDigitalStoresUser || isTapAdmin) && (
                <>
                  <TapNavMenuSection
                    path="/tprExceptions"
                    to={buildNavTo("/tprExceptions")}
                    isActive={activePanel === "/tprExceptions"}
                    mainSection={
                      <Icon
                        altText="tprExceptions"
                        faPackage="fas"
                        iconName="fa-briefcase"
                        iconType="fa"
                        title="tprExceptions"
                      />
                    }
                  >
                    TPR Exceptions
                  </TapNavMenuSection>
                  <br />
                </>
              )}
              {(isDigitalStoresUser || isTapAdmin) && (
                <>
                  <TapNavMenuSection
                    path="/ppt"
                    to={buildNavTo("/ppt")}
                    isActive={activePanel === "/ppt"}
                    mainSection={
                      <Icon
                        altText="Promo Planning"
                        faPackage="fas"
                        iconName="fa-chart-line"
                        iconType="fa"
                        title="Promo Planning"
                      />
                    }
                  >
                    PPM
                  </TapNavMenuSection>
                  <br />
                </>
              )}
              {isAdmin && (
                <>
                  <TapNavMenuSection
                    path="/users"
                    to={buildNavTo("/users")}
                    isActive={activePanel === "/users"}
                    mainSection={
                      <Icon
                        altText="users"
                        faPackage="fas"
                        iconName="fa-users"
                        iconType="fa"
                        title="users"
                      />
                    }
                  >
                    Users
                  </TapNavMenuSection>
                  <br />
                </>
              )}
            </ExpandableMenu>
          </SecondaryPanel>
        </DualPanelLayout>

        <AppFooter />
      </div>
    </>
  );
};
